// ─── Intercompany Engine v2 — types + Zod-schemas ──────────────────────────
//
// Datamodel: zie packages/db/prisma/schema.prisma — IntercompanyRule +
// IntercompanyAssignment + IntercompanyTransaction + ManagementFeeRule +
// IntercompanyPeriod + EmployeeCompanyHistory + FuelCard / FuelCardTransaction.
// Spec: docs/PLAN-IC-ENGINE-EN-ONBOARDING-V2.md §2 + uploads/16-intercompany-engine 1.md.
//
// Pure types-laag: geen Prisma-imports. State-machine + berekeningen wonen in
// packages/utils/src/intercompany-calculations.ts.

import { z } from 'zod'
import { idParam } from './id-validators'
import { partialZonderDefaults } from './zod-patch'

// ─── Enums als string-unions ─────────────────────────────────────────────────

export const IC_RULE_TYPES = [
  'MEDEWERKER',
  'MANAGEMENT_FEE',
  'AUTO',
  'TANKPAS',
  'MIDDEL',
  'VASTE_KOSTEN',
  'CORRECTIE',
] as const
export type ICRuleType = (typeof IC_RULE_TYPES)[number]

export const IC_BEREKENINGSMETHODES = [
  'UURTARIEF',
  'VAST_BEDRAG_MAAND',
  'PERCENTAGE_OMZET',
  'LEASEPRIJS_PLUS_KOSTEN',
  'VERDEELSLEUTEL_FTE',
  'VERDEELSLEUTEL_UREN',
  'VERDEELSLEUTEL_OMZET',
  'HANDMATIG',
] as const
export type ICBerekeningsmethode = (typeof IC_BEREKENINGSMETHODES)[number]

export const IC_OPSLAG_TYPES = ['GEEN', 'PERCENTAGE', 'VAST_BEDRAG', 'STAFFEL'] as const
export type ICOpslagType = (typeof IC_OPSLAG_TYPES)[number]

// Volledige enum-mirror — alle leden inclusief backward-compatibele PER_WERKBON en JAARLIJKS.
// Gebruik IC_AFREKEN_FREQUENTIES voor z.enum (accepteert ook legacy-waarden bij PATCH).
// Gebruik IC_AFREKEN_FREQUENTIES_SELECTEERBAAR voor create/edit UI-selects (4 opties).
export const IC_AFREKEN_FREQUENTIES = [
  'PER_WERKBON',
  'MAANDELIJKS',
  'WEKELIJKS',
  'KWARTAAL',
  'JAARLIJKS',
  'EENMALIG',
] as const
export type ICAfrekenFrequentie = (typeof IC_AFREKEN_FREQUENTIES)[number]

// De 4 opties die gebruikers kunnen kiezen bij aanmaken/bewerken van een regel.
// PER_WERKBON en JAARLIJKS zijn enum-leden voor backward-compatibiliteit (bestaande regels),
// maar niet selecteerbaar in de UI.
export const IC_AFREKEN_FREQUENTIES_SELECTEERBAAR = [
  'MAANDELIJKS',
  'WEKELIJKS',
  'KWARTAAL',
  'EENMALIG',
] as const
export type ICAfrekenFrequentieSelecteerbaar = (typeof IC_AFREKEN_FREQUENTIES_SELECTEERBAAR)[number]

export const IC_FEE_METHODES = [
  'VAST_BEDRAG',
  'PERCENTAGE_OMZET',
  'PERCENTAGE_KOSTEN',
  'VERDEELSLEUTEL_FTE',
  'VERDEELSLEUTEL_UREN',
  'VERDEELSLEUTEL_OMZET',
  'HANDMATIG_PER_BV',
  'STAFFEL_OMZET',
] as const
export type ICFeeMethode = (typeof IC_FEE_METHODES)[number]

export const IC_OBJECT_TYPES = ['MEDEWERKER', 'VOERTUIG', 'TANKPAS', 'MIDDEL'] as const
export type ICObjectType = (typeof IC_OBJECT_TYPES)[number]

export const IC_ASSIGNMENT_STATUSES = ['ACTIEF', 'BEEINDIGD', 'GEPLAND'] as const
export type ICAssignmentStatus = (typeof IC_ASSIGNMENT_STATUSES)[number]

export const IC_TRANSACTION_STATUSES = [
  'TE_BEREKENEN',
  'WACHT_OP_LEASEPRIJS',
  'WACHT_OP_OMZETCIJFERS',
  'WACHT_OP_GOEDKEURING_UREN',
  'WACHT_OP_VERDEELSLEUTEL',
  'UREN_GOEDGEKEURD',
  'BEREKEND',
  'TE_KEUREN',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'GEFACTUREERD',
  'BETAALD',
  'GEBLOKKEERD',
  'GECORRIGEERD',
  'GEANNULEERD',
] as const
export type ICTransactionStatus = (typeof IC_TRANSACTION_STATUSES)[number]

export const IC_PERIOD_STATUSES = [
  'OPEN',
  'IN_BEREKENING',
  'KLAAR_VOOR_AFSLUITING',
  'AFGESLOTEN',
  'HEROPEND',
] as const
export type ICPeriodStatus = (typeof IC_PERIOD_STATUSES)[number]

// ─── Label-records voor UI-badges ────────────────────────────────────────────

export const IC_RULE_TYPE_LABELS: Record<ICRuleType, string> = {
  MEDEWERKER: 'Medewerker-uitleen',
  MANAGEMENT_FEE: 'Management fee',
  AUTO: 'Auto',
  TANKPAS: 'Tankpas',
  MIDDEL: 'Middel',
  VASTE_KOSTEN: 'Vaste kosten',
  CORRECTIE: 'Correctie',
}

export const IC_TRANSACTION_STATUS_LABELS: Record<ICTransactionStatus, string> = {
  TE_BEREKENEN: 'Te berekenen',
  WACHT_OP_LEASEPRIJS: 'Wacht op leaseprijs',
  WACHT_OP_OMZETCIJFERS: 'Wacht op omzetcijfers',
  WACHT_OP_GOEDKEURING_UREN: 'Wacht op uren-goedkeuring',
  WACHT_OP_VERDEELSLEUTEL: 'Wacht op verdeelsleutel',
  UREN_GOEDGEKEURD: 'Uren goedgekeurd',
  BEREKEND: 'Berekend',
  TE_KEUREN: 'Te keuren',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgekeurd',
  GEFACTUREERD: 'Gefactureerd',
  BETAALD: 'Betaald',
  GEBLOKKEERD: 'Geblokkeerd',
  GECORRIGEERD: 'Gecorrigeerd',
  GEANNULEERD: 'Geannuleerd',
}

export const IC_PERIOD_STATUS_LABELS: Record<ICPeriodStatus, string> = {
  OPEN: 'Open',
  IN_BEREKENING: 'In berekening',
  KLAAR_VOOR_AFSLUITING: 'Klaar voor afsluiting',
  AFGESLOTEN: 'Afgesloten',
  HEROPEND: 'Heropend',
}

export const IC_AFREKEN_FREQUENTIE_LABELS: Record<ICAfrekenFrequentie, string> = {
  PER_WERKBON: 'Per werkbon',
  MAANDELIJKS: 'Maandelijks',
  WEKELIJKS: 'Wekelijks',
  KWARTAAL: 'Kwartaallijks',
  JAARLIJKS: 'Jaarlijks',
  EENMALIG: 'Eenmalig',
}

export const IC_TRANSACTION_STATUS_BADGE_VARIANT: Record<
  ICTransactionStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  TE_BEREKENEN: 'secondary',
  WACHT_OP_LEASEPRIJS: 'warning',
  WACHT_OP_OMZETCIJFERS: 'warning',
  WACHT_OP_GOEDKEURING_UREN: 'warning',
  WACHT_OP_VERDEELSLEUTEL: 'warning',
  UREN_GOEDGEKEURD: 'info',
  BEREKEND: 'info',
  TE_KEUREN: 'info',
  GOEDGEKEURD: 'success',
  AFGEKEURD: 'danger',
  GEFACTUREERD: 'success',
  BETAALD: 'success',
  GEBLOKKEERD: 'danger',
  GECORRIGEERD: 'secondary',
  GEANNULEERD: 'secondary',
}

// ─── Zod-schemas voor API-input ──────────────────────────────────────────────

export const icStaffelEntrySchema = z.object({
  van: z.number().min(0),
  tot: z.number().min(0),
  percentage: z.number().min(0).max(100).optional(),
  vastBedrag: z.number().min(0).optional(),
})
export type ICStaffelEntry = z.infer<typeof icStaffelEntrySchema>

// Basis-object zonder cross-field-refine, zodat `updateICRuleSchema` er een
// schone `.partial()` op kan doen (een ZodEffects van `.superRefine` ondersteunt
// `.partial()` niet).
const icRuleBaseSchema = z.object({
  type: z.enum(IC_RULE_TYPES),
  bronBvId: idParam(),
  gebruikendeBvId: idParam(),
  // Bij type MEDEWERKER verplicht (zie superRefine); bij andere types genegeerd
  // en serverside op null geforceerd.
  employeeId: idParam().nullable().optional(),
  berekeningsmethode: z.enum(IC_BEREKENINGSMETHODES),
  uurtarief: z.number().nonnegative().optional(),
  vastBedragMaand: z.number().nonnegative().optional(),
  percentageOmzet: z.number().min(0).max(100).optional(),
  opslagType: z.enum(IC_OPSLAG_TYPES).default('GEEN'),
  opslagPercentage: z.number().min(0).max(100).optional(),
  opslagVastBedrag: z.number().nonnegative().optional(),
  opslagStaffel: z.array(icStaffelEntrySchema).optional(),
  frequentie: z.enum(IC_AFREKEN_FREQUENTIES).default('MAANDELIJKS'),
  geldigVanaf: z.string(), // ISO datum
  geldigTot: z.string().nullable().optional(),
  toelichting: z.string().optional(),
})

export const createICRuleSchema = icRuleBaseSchema
  // Cross-field-regel: een MEDEWERKER-regel moet aan een concrete medewerker
  // gekoppeld zijn. De refine vuurt alléén wanneer `type === 'MEDEWERKER'` in de
  // payload zit. Het PATCH-pad gebruikt `icRuleBaseSchema.partial()` (zonder deze
  // refine), dus een partial update zonder `type` blijft valideren.
  .superRefine((val, ctx) => {
    if (val.type === 'MEDEWERKER' && (val.employeeId === undefined || val.employeeId === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['employeeId'],
        message: 'Kies een medewerker voor een medewerker-regel.',
      })
    }
  })
export type CreateICRuleInput = z.infer<typeof createICRuleSchema>

export const updateICRuleSchema = partialZonderDefaults(icRuleBaseSchema).extend({
  actief: z.boolean().optional(),
})
export type UpdateICRuleInput = z.infer<typeof updateICRuleSchema>

export const createICAssignmentSchema = z.object({
  ruleId: idParam().optional(),
  objectType: z.enum(IC_OBJECT_TYPES),
  objectId: idParam(),
  gebruikendeBvId: idParam(),
  startDatum: z.string(),
  eindDatum: z.string().nullable().optional(),
  pctGebruikt: z.number().min(0).max(100).optional(),
  toelichting: z.string().optional(),
})
export type CreateICAssignmentInput = z.infer<typeof createICAssignmentSchema>

export const createEmployeeCompanyHistorySchema = z.object({
  // Geen .cuid()-check: seed-entities (`seed_entity_*`) en geïmporteerde
  // medewerkers kunnen non-cuid IDs hebben. De DB-FK valideert de werkelijke
  // existence, dus min(1) is voldoende.
  employeeId: z.string().min(1),
  bvId:       z.string().min(1),
  startDatum: z.string(),
  reden:      z.string().optional(),
})
export type CreateEmployeeCompanyHistoryInput = z.infer<typeof createEmployeeCompanyHistorySchema>

export const createICPeriodSchema = z.object({
  jaar: z.number().int().min(2020).max(2100),
  maand: z.number().int().min(1).max(12),
  toelichting: z.string().optional(),
})
export type CreateICPeriodInput = z.infer<typeof createICPeriodSchema>

export const decideICTransactionSchema = z.object({
  reden: z.string().optional(),
})
export type DecideICTransactionInput = z.infer<typeof decideICTransactionSchema>

export const csvFuelImportSchema = z.object({
  fuelCardId: idParam(),
  rows: z.array(
    z.object({
      datum: z.string(),
      liters: z.number(),
      bedragExclBtw: z.number(),
      btwBedrag: z.number(),
      bedragInclBtw: z.number(),
      locatie: z.string().optional(),
    }),
  ),
})

// ─── DTOs voor API-responses ────────────────────────────────────────────────

export interface ICRuleDTO {
  id: string
  type: ICRuleType
  bronBvId: string
  gebruikendeBvId: string
  employeeId: string | null
  berekeningsmethode: ICBerekeningsmethode
  uurtarief: number | null
  vastBedragMaand: number | null
  percentageOmzet: number | null
  opslagType: ICOpslagType
  opslagPercentage: number | null
  opslagVastBedrag: number | null
  opslagStaffel: ICStaffelEntry[] | null
  frequentie: ICAfrekenFrequentie
  geldigVanaf: string
  geldigTot: string | null
  actief: boolean
  toelichting: string | null
  createdAt: string
  updatedAt: string
}

export interface ICTransactionDTO {
  id: string
  code: string
  periodeId: string
  ruleId: string | null
  type: ICRuleType
  bronBvId: string
  gebruikendeBvId: string
  status: ICTransactionStatus
  subtotaal: number
  opslag: number
  btwBedrag: number
  btwPercentage: number | null
  totaal: number
  blokkadeReden: string | null
  goedgekeurdDoor: string | null
  goedgekeurdOp: string | null
  afgekeurdDoor: string | null
  afgekeurdOp: string | null
  afgekeurdReden: string | null
  origineelTxId: string | null
  createdAt: string
  updatedAt: string
}

export interface ICPeriodDTO {
  id: string
  jaar: number
  maand: number
  status: ICPeriodStatus
  afgeslotenOp: string | null
  afgeslotenDoor: string | null
  heropendOp: string | null
  heropendDoor: string | null
  toelichting: string | null
}

export interface EmployeeCompanyHistoryDTO {
  id: string
  employeeId: string
  bvId: string
  startDatum: string
  eindDatum: string | null
  reden: string | null
}

// ─── State-machines voor IC-rule + IC-transaction + IC-period ───────────────
//
// State-machines staan hier in types (geen Prisma-import) zodat zowel API als
// frontend dezelfde transition-set kunnen gebruiken. @streep/utils re-exporteert
// + biedt is*TransitionAllowed-helpers waar dat handig is.

export const IC_RULE_TRANSITION_MAP: Record<'ACTIEF' | 'INACTIEF', Array<'ACTIEF' | 'INACTIEF'>> = {
  ACTIEF: ['INACTIEF'],
  INACTIEF: ['ACTIEF'],
}

// Transitions per spec §6 (transactie-statusmachine).
export const IC_TRANSACTION_TRANSITION_MAP: Record<ICTransactionStatus, ICTransactionStatus[]> = {
  TE_BEREKENEN: ['BEREKEND', 'WACHT_OP_LEASEPRIJS', 'WACHT_OP_OMZETCIJFERS', 'WACHT_OP_GOEDKEURING_UREN', 'WACHT_OP_VERDEELSLEUTEL', 'GEBLOKKEERD', 'GEANNULEERD'],
  WACHT_OP_LEASEPRIJS: ['TE_BEREKENEN', 'GEBLOKKEERD', 'GEANNULEERD'],
  WACHT_OP_OMZETCIJFERS: ['TE_BEREKENEN', 'GEBLOKKEERD', 'GEANNULEERD'],
  WACHT_OP_GOEDKEURING_UREN: ['UREN_GOEDGEKEURD', 'GEBLOKKEERD', 'GEANNULEERD'],
  WACHT_OP_VERDEELSLEUTEL: ['TE_BEREKENEN', 'GEBLOKKEERD', 'GEANNULEERD'],
  UREN_GOEDGEKEURD: ['BEREKEND', 'GEBLOKKEERD', 'GEANNULEERD'],
  BEREKEND: ['TE_KEUREN', 'GEBLOKKEERD', 'GEANNULEERD'],
  TE_KEUREN: ['GOEDGEKEURD', 'AFGEKEURD', 'GEBLOKKEERD'],
  GOEDGEKEURD: ['GEFACTUREERD', 'GECORRIGEERD'],
  AFGEKEURD: ['TE_BEREKENEN', 'GEANNULEERD'],
  GEFACTUREERD: ['BETAALD', 'GECORRIGEERD'],
  BETAALD: ['GECORRIGEERD'],
  GEBLOKKEERD: ['TE_BEREKENEN', 'GEANNULEERD'],
  GECORRIGEERD: [], // terminal
  GEANNULEERD: [], // terminal
}

export const IC_PERIOD_TRANSITION_MAP: Record<ICPeriodStatus, ICPeriodStatus[]> = {
  OPEN: ['IN_BEREKENING'],
  IN_BEREKENING: ['KLAAR_VOOR_AFSLUITING', 'OPEN'],
  KLAAR_VOOR_AFSLUITING: ['AFGESLOTEN', 'IN_BEREKENING'],
  AFGESLOTEN: ['HEROPEND'],
  HEROPEND: ['IN_BEREKENING'],
}

export function isICTransactionTransitionAllowed(
  from: ICTransactionStatus,
  to: ICTransactionStatus,
): boolean {
  return IC_TRANSACTION_TRANSITION_MAP[from].includes(to)
}

export function isICPeriodTransitionAllowed(from: ICPeriodStatus, to: ICPeriodStatus): boolean {
  return IC_PERIOD_TRANSITION_MAP[from].includes(to)
}

// ─── FO-023 (batch-15) — transactie-acties: status + permissie -> beschikbaarheid ─
//
// Eén bron van waarheid voor zowel de UI (welke actie kan, en met welke reden niet)
// als de leesbaarheid van de state-machine. De API blijft leidend (state-machine +
// requirePermission); dit spiegelt die regels zodat een knop nooit "klikbaar is maar
// niets doet": een actie die niet mag is zichtbaar-maar-uitgeschakeld mét uitleg.

export const IC_TRANSACTION_ACTIONS = ['goedkeuren', 'afkeuren', 'deblokkeren', 'corrigeren'] as const
export type ICTransactionAction = (typeof IC_TRANSACTION_ACTIONS)[number]

export const IC_TRANSACTION_ACTION_LABELS: Record<ICTransactionAction, string> = {
  goedkeuren: 'Goedkeuren',
  afkeuren: 'Afkeuren',
  deblokkeren: 'Deblokkeren',
  corrigeren: 'Corrigeren',
}

/** Welke transactie-status(sen) een actie toestaat (spiegelt de state-machine + de API-routes). */
export const IC_TRANSACTION_ACTION_STATUSES: Record<ICTransactionAction, readonly ICTransactionStatus[]> = {
  goedkeuren: ['TE_KEUREN'],
  afkeuren: ['TE_KEUREN'],
  deblokkeren: ['GEBLOKKEERD'],
  corrigeren: ['GOEDGEKEURD', 'GEFACTUREERD', 'BETAALD'],
}

/**
 * Welk INTERCOMPANY-recht een actie vereist (spiegelt de requirePermission-gates in
 * apps/api/src/routes/ic/transactions.ts). Per FO-023 §2 vereisen alle transactie-
 * acties (incl. deblokkeren) `canApprove`.
 */
export const IC_TRANSACTION_ACTION_PERMISSION: Record<ICTransactionAction, 'canApprove' | 'canEdit'> = {
  goedkeuren: 'canApprove',
  afkeuren: 'canApprove',
  deblokkeren: 'canApprove',
  corrigeren: 'canApprove',
}

export interface ICTransactionPermissions {
  canApprove: boolean
  canEdit: boolean
}

export interface ICTransactionActionAvailability {
  action: ICTransactionAction
  label: string
  /** Status laat deze actie toe (los van permissie). Bepaalt of de actie *relevant* is voor deze rij. */
  statusOk: boolean
  /** Gebruiker heeft het vereiste recht. */
  permOk: boolean
  /** statusOk && permOk. */
  allowed: boolean
  /** Mensvriendelijke reden waarom de actie niet kan (alleen gezet als !allowed). */
  reason: string | null
}

/**
 * Beschikbaarheid van alle vier transactie-acties voor een status + rechten. De UI
 * rendert doorgaans alleen de status-relevante acties (statusOk) en schakelt ze uit
 * met `reason` als het vereiste recht ontbreekt.
 */
export function getICTransactionActionAvailability(
  status: ICTransactionStatus,
  perms: ICTransactionPermissions,
): ICTransactionActionAvailability[] {
  return IC_TRANSACTION_ACTIONS.map((action) => {
    const statusOk = IC_TRANSACTION_ACTION_STATUSES[action].includes(status)
    const benodigdRecht = IC_TRANSACTION_ACTION_PERMISSION[action]
    const permOk = benodigdRecht === 'canApprove' ? perms.canApprove : perms.canEdit
    const allowed = statusOk && permOk
    let reason: string | null = null
    if (!statusOk) {
      const toegestaan = IC_TRANSACTION_ACTION_STATUSES[action]
        .map((s) => IC_TRANSACTION_STATUS_LABELS[s])
        .join(' / ')
      reason = `Alleen mogelijk bij status: ${toegestaan} (nu: ${IC_TRANSACTION_STATUS_LABELS[status]}).`
    } else if (!permOk) {
      reason =
        benodigdRecht === 'canApprove'
          ? 'Je hebt geen goedkeur-recht voor Intercompany.'
          : 'Je hebt geen bewerk-recht voor Intercompany.'
    }
    return { action, label: IC_TRANSACTION_ACTION_LABELS[action], statusOk, permOk, allowed, reason }
  })
}

// Gedeeld request-schema voor de corrigeer-actie (API + form, één bron). Geld reist
// als number; de API rekent in Decimal. De bedragen zijn optioneel: leeg = neem het
// originele bedrag over (pure terugboeking, credit + identieke debet).
export const corrigeerICTransactieSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden van minstens 3 tekens.').max(500),
  nieuwSubtotaal: z.number().nonnegative().optional(),
  nieuwOpslag: z.number().nonnegative().optional(),
  nieuwBtwPercentage: z.number().min(0).max(100).optional(),
  omschrijving: z.string().max(500).optional(),
})
export type CorrigeerICTransactieInput = z.infer<typeof corrigeerICTransactieSchema>
